import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { isTokenPage } from "./utils";

export type DisplayMode = "system" | "light" | "dark";
const KEY = "bridewell.displayMode";
/** Light is the default (owner's decision, 2026-09-10): dark only when chosen, or when "System" is chosen on a dark OS. */
export const DEFAULT_MODE: DisplayMode = "light";
/** Browser-chrome colours (`<meta name="theme-color">`): the Classic theme's `--bg` in each mode. Mirrored in index.html's bootstrap script. */
export const THEME_COLOR = { light: "#FAF7F5", dark: "#120A17" } as const;

interface ThemeCtx { mode: DisplayMode; setMode: (m: DisplayMode) => void; isDark: boolean }
const Ctx = createContext<ThemeCtx | null>(null);

const DARK_MQ = "(prefers-color-scheme: dark)";

function systemDark() {
  return window.matchMedia(DARK_MQ).matches;
}

function readStored(): DisplayMode {
  try {
    const v = localStorage.getItem(KEY);
    return v === "system" || v === "light" || v === "dark" ? v : DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

/**
 * Applies the resolved appearance to the document. index.html runs the same logic inline before
 * first paint (from the stored choice only), so this is a no-op on load and only matters on change.
 */
function apply(isDark: boolean) {
  const html = document.documentElement;
  html.classList.toggle("dark", isDark);
  // Native controls (scrollbars, form widgets) and the browser chrome follow the app, not the OS.
  html.style.colorScheme = isDark ? "dark" : "light";
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", isDark ? THEME_COLOR.dark : THEME_COLOR.light);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<DisplayMode>(readStored);
  const [sys, setSys] = useState(systemDark);

  useEffect(() => {
    const mq = window.matchMedia(DARK_MQ);
    const on = () => setSys(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // Another tab changed the choice: follow it, so two open tabs never disagree.
  useEffect(() => {
    const on = (e: StorageEvent) => { if (e.key === KEY) setModeState(readStored()); };
    window.addEventListener("storage", on);
    return () => window.removeEventListener("storage", on);
  }, []);

  const isDark = mode === "dark" || (mode === "system" && sys);
  useEffect(() => apply(isDark), [isDark]);

  const value = useMemo<ThemeCtx>(() => ({
    mode,
    isDark,
    setMode: (m) => {
      setModeState(m);
      // Cookie Policy §2: guest token pages store nothing. Check at choice time after SPA navigation.
      // Keep this the only write path: effects must never persist an in-memory guest choice later.
      if (isTokenPage(window.location.pathname)) return;
      try { localStorage.setItem(KEY, m); } catch { /* private mode */ }
    },
  }), [mode, isDark]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useTheme outside ThemeProvider");
  return v;
}
